import { type FC, useEffect, useRef, useState } from "react";
import { DialogComponent, Popover, TriggerButton, TriggerButtonSize } from "widgets";
import { useOverlay } from "widgets/hooks";

import { ContigSelectionCombobox } from "./ContigSelectionCombobox.tsx";

interface TrackConfigurationDialogProps {
  groupId: string;
}

export const TrackGroupConfigurationDialog: FC<TrackConfigurationDialogProps> = ({ groupId }) => {
  const { overlayProps, state } = useOverlay({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeightPx] = useState(0);

  useEffect(() => {
    if (popoverRef.current) {
      const maxHeight = getMaxHeightAsNumber(popoverRef.current);
      setMaxHeightPx(maxHeight);
    }
  }, [state.isOpen]);

  return (
    <>
      <TriggerButton
        size={TriggerButtonSize.Small}
        icon={"swap_horiz"}
        onPress={() => state.open()}
        label="Change Contig"
        ref={triggerRef}
      />
      <Popover
        state={state}
        placement={"bottom end"}
        {...overlayProps}
        triggerRef={triggerRef}
        offset={10}
        ref={popoverRef}
      >
        <DialogComponent
          closeFn={() => state.close()}
          data-testid="track-group-configuration"
          width={250}
          contentStyle={{ overflow: "hidden" }}
        >
          <ContigSelectionCombobox groupId={groupId} close={state.close} maxHeight={maxHeight} />
        </DialogComponent>
      </Popover>
    </>
  );
};

const getMaxHeightAsNumber = (element: HTMLElement) => {
  const computedStyle = window.getComputedStyle(element);
  const maxHeightStr = computedStyle.getPropertyValue("max-height");

  // Handle different units
  if (maxHeightStr === "none") return Infinity;
  if (maxHeightStr.endsWith("px")) {
    return parseFloat(maxHeightStr);
  }
  if (maxHeightStr.endsWith("%")) {
    // Calculate percentage of parent
    const parentHeight = element.parentElement?.offsetHeight ?? 0;
    return (parseFloat(maxHeightStr) / 100) * parentHeight;
  }

  return parseFloat(maxHeightStr) || 0;
};
